import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react';

import { useIntl } from 'src/hooks/intl';
import { useHistory__UNSAFE, useParams } from 'src/router/hooks';
import urls from 'src/urls';

import {
  DiffStateContext,
  MultiCommitSelection,
} from '../tabs/diff/diff-state-context';

import { CommitItem } from './commit-item';

type CommitListProps = {
  commits: BB.Commit[];
  closeSelector: () => void;
  multiCommitSelection: MultiCommitSelection;
  setMultiCommitSelection: Dispatch<SetStateAction<MultiCommitSelection>>;
  setInitialMultiSelectState: Dispatch<SetStateAction<MultiCommitSelection>>;
  resetToInitialSelection: () => void;
  activeIndex: number;
  setActiveIndex: Dispatch<SetStateAction<number>>;
  onIRItemSelectEvent: (selectedDropdownItem: string) => void;
};

export type CommitItemsSelectedState = {
  [key: string]: boolean;
};

export const CommitItemsList = (props: CommitListProps): JSX.Element => {
  const {
    commits,
    closeSelector,
    multiCommitSelection,
    setMultiCommitSelection,
    setInitialMultiSelectState,
    resetToInitialSelection,
    activeIndex,
    setActiveIndex,
    onIRItemSelectEvent,
  } = props;

  const { iterativeReviewSelectedDiff, setIterativeReviewSelectedDiff } =
    useContext(DiffStateContext);

  const intl = useIntl();

  const history = useHistory__UNSAFE();

  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();

  const diffTabUrl = urls.ui.pullRequestTab(
    repositoryOwner,
    repositorySlug,
    pullRequestId,
    'diff'
  );

  const updateHistory = (sourceHash: string, destinationHash?: string) => {
    if (destinationHash) {
      history.replace(`${diffTabUrl}/${sourceHash}..${destinationHash}`);
    } else {
      history.replace(`${diffTabUrl}/${sourceHash}`);
    }
  };

  // reset selection when shift key is released
  useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      // this only happens when the user is currently mid-selection (active index >= 0)
      if (event.key === 'Shift' && activeIndex >= 0) {
        resetToInitialSelection();
      }
    };

    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [activeIndex, resetToInitialSelection]);

  return (
    <>
      {commits.map((commit, index) =>
        CommitItem({
          intl,
          commit,
          index,
          activeIndex,
          setActiveIndex,
          multiCommitSelection,
          setMultiCommitSelection,
          onDiffSelected: closeSelector,
          updateHistory,
          iterativeReviewSelectedDiff,
          setIterativeReviewSelectedDiff,
          setInitialMultiSelectState,
          onIRItemSelectEvent,
        })
      )}
    </>
  );
};
